import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { fetchCollection, joinWaitlist } from "@/lib/api";
import Seo from "@/components/seo/Seo";
import WaitlistModal from "@/components/WaitlistModal";

const STAGE_COPY = {
  Wash: {
    overline: "01 — Wash",
    title: "A quiet rinse, before the pulse falls.",
    body:
      "First gesture. Warm water, a palm of magnesium wash, a slow sweep across the shoulders and the back of the neck. The day begins to lift from the skin.",
    bg: "bg-sand",
    accent: "text-walnut",
  },
  Nourish: {
    overline: "02 — Nourish",
    title: "Slow pressure, slower absorption.",
    body:
      "Massaged into the legs and shoulders after the wash. The mineral finds the muscle. The body learns, again, what unclenching feels like.",
    bg: "bg-walnut text-cream",
    accent: "text-ochre",
  },
  Breathe: {
    overline: "03 — Breathe",
    title: "A short mist, a long exhale.",
    body:
      "Two mists across the pillow, drawn up on the first slow exhale. The room softens before the body does.",
    bg: "bg-wine text-cream",
    accent: "text-terracotta-light",
  },
  Sip: {
    overline: "04 — Sip",
    title: "An hour before bed. Loose-leaf. Slow.",
    body:
      "Steeped quietly. Chamomile, valerian, passionflower and a thread of rose. The signal to the body that the day is closing.",
    bg: "bg-cream",
    accent: "text-moss-dark",
  },
  Daily: {
    overline: "05 — Daily",
    title: "The daily close, repeatable as breath.",
    body:
      "One sachet dissolved in warm water, fifteen minutes before bed. Magnesium, ashwagandha, l-theanine. The practice becomes unremarkable.",
    bg: "bg-ochre/25",
    accent: "text-ochre-dark",
  },
};

const WIDE_BAND = "/generated/a-sweeping-wide-cinematic-overhead-edito-d3dfd0.png";
const BATH_SCENE = "/generated/a-warm-bath-scene-from-above-a-deep-sto-bd87e5.png";

export default function RitualDetail() {
  const { slug } = useParams();

  // Only "rest" currently has a product collection. Everything else shows a coming-soon state.
  if (slug !== "rest") {
    return <ComingSoonRitual slug={slug} />;
  }
  return <RestRitual />;
}

function RestRitual() {
  const [ritual, setRitual] = useState(null);
  const [waitlistFor, setWaitlistFor] = useState(null);

  useEffect(() => {
    fetchCollection("rest-ritual").then(setRitual).catch(() => {});
  }, []);

  const products = ritual?.products || [];
  const stageOrder = ["Wash", "Nourish", "Breathe", "Sip", "Daily"];
  const grouped = useMemo(() => {
    const map = {};
    for (const p of products) map[p.stage || "Other"] = p;
    return map;
  }, [products]);

  // Collection waitlist pseudo-product for the top-of-page CTA
  const collectionPseudo = {
    slug: "collection:rest-ritual",
    name: "The Rest Collection",
    tagline:
      "The full Rest ritual — Magnesium Body Wash, Body Butter, Sleep Pillow Spray, Sleep Tea and PM Wellness Sachet. Join once; be first to every piece.",
  };

  return (
    <div data-testid="ritual-rest">
      <Seo
        path="/rituals/rest"
        title="Rest — The Ritual"
        description="A modern ritual for slowing down. Five gestures — Wash, Nourish, Breathe, Sip, Daily. Magnesium Body Wash, Body Butter, Sleep Pillow Spray, Sleep Tea and PM Wellness Sachet."
        image={grouped?.Nourish?.cover_image || grouped?.Wash?.cover_image}
      />

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-24 px-6 md:px-12 lg:px-16 bg-bone">
        <Link to="/rituals" className="overline inline-flex items-center gap-2 text-terracotta hover:text-ink mb-10">
          ← All Rituals
        </Link>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <p className="overline mb-6 text-terracotta" data-testid="rest-overline">The Rest Ritual · 01 · Coming December 2026</p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-[8rem] font-light leading-[0.92] tracking-[-0.03em] text-ink">
              A ritual<br />
              for <em className="italic text-terracotta">slowing</em><br />
              <span className="text-forest">down.</span>
            </h1>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 space-y-6">
            <p className="font-display italic text-xl md:text-2xl text-charcoal leading-snug">
              Rest is coming December 2026.
            </p>
            <p className="text-charcoal text-base md:text-lg font-light leading-[1.7]">
              Our first collection of considered rituals for slower evenings,
              deeper rest, and everyday restoration. Join the waitlist for
              first access.
            </p>
            <button
              onClick={() => setWaitlistFor(collectionPseudo)}
              data-testid="collection-waitlist-cta"
              className="inline-flex items-center gap-2 text-[0.72rem] tracking-[0.24em] uppercase bg-forest text-cream px-6 py-4 hover:bg-terracotta transition-colors"
            >
              Join the Rest waitlist <ArrowUpRight size={14} />
            </button>
          </div>
        </div>
      </section>

      {/* Full-bleed cinematic hero band */}
      <section className="relative overflow-hidden bg-ink">
        <div className="aspect-[16/8] md:aspect-[16/7]">
          <img src={WIDE_BAND} alt="The Rest ritual, laid out" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-6 md:px-12 lg:px-16 py-10 md:py-14 bg-gradient-to-t from-ink/80 via-ink/20 to-transparent">
            <p className="overline text-cream/80 mb-3">Topical · Ingestible · Sensory</p>
            <h2 className="font-display text-2xl md:text-4xl lg:text-5xl text-cream font-light italic leading-tight max-w-3xl">
              Five gestures, repeated — until the body remembers.
            </h2>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section>
        {stageOrder.map((stage, idx) => {
          const p = grouped[stage];
          const copy = STAGE_COPY[stage];
          const alt = idx % 2 === 1;
          const isWaitlist = p?.is_waitlist;
          if (!p || !copy) return null;
          const isDark = copy.bg.includes("text-cream");

          return (
            <article
              key={stage}
              data-testid={`stage-${stage.toLowerCase()}`}
              className={`border-t ${isDark ? "border-ink/30" : "border-hairline"} ${copy.bg}`}
            >
              <div className="px-6 md:px-12 lg:px-16 py-16 md:py-28">
                <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${alt ? "lg:[&>*:first-child]:order-2" : ""}`}>
                  {/* Image */}
                  <div className="lg:col-span-6 img-zoom aspect-[4/5] overflow-hidden">
                    <img
                      src={p.cover_image}
                      alt={p.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Copy */}
                  <div className="lg:col-span-6 lg:px-6">
                    <p className={`overline mb-6 ${copy.accent}`}>{copy.overline}</p>
                    <h2 className={`font-display text-4xl md:text-6xl lg:text-7xl font-light leading-[0.98] tracking-[-0.02em] max-w-md ${isDark ? "" : "text-ink"}`}>
                      {copy.title}
                    </h2>
                    <p className={`mt-8 font-light leading-[1.75] text-base md:text-lg max-w-md ${isDark ? "opacity-85" : "text-charcoal"}`}>
                      {copy.body}
                    </p>

                    {/* Product card — all waitlist in pre-launch mode */}
                    <div className={`mt-10 border-t pt-8 ${isDark ? "border-cream/25" : "border-ink/15"}`}>
                      <div className="flex items-baseline justify-between gap-6 flex-wrap mb-2">
                        <h3 className="font-display text-2xl md:text-3xl">{p.name}</h3>
                        <p className={`overline ${copy.accent}`}>Coming Dec 2026</p>
                      </div>
                      <p className={`font-display italic text-base md:text-lg mb-6 ${isDark ? "opacity-80" : "text-charcoal"}`}>
                        {p.tagline}
                      </p>
                      <button
                        onClick={() => setWaitlistFor(p)}
                        data-testid={`waitlist-cta-${p.slug}`}
                        className={`inline-flex items-center gap-2 text-[0.72rem] tracking-[0.24em] uppercase px-6 py-3 transition-colors border ${
                          isDark
                            ? "border-cream text-cream hover:bg-cream hover:text-ink"
                            : "border-terracotta text-terracotta hover:bg-terracotta hover:text-cream"
                        }`}
                      >
                        Join this product's waitlist <ArrowUpRight size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </section>

      {/* Mid-ritual pull — bath image + forest overlay */}
      <section className="relative overflow-hidden border-t border-forest/40 bg-forest text-cream">
        <div className="absolute inset-0 opacity-30">
          <img src={BATH_SCENE} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-forest/70" />
        </div>
        <div className="relative px-6 md:px-12 lg:px-16 py-28 md:py-40">
          <div className="max-w-4xl">
            <p className="overline mb-8 text-terracotta-light">On repetition</p>
            <p className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight font-light text-cream">
              "A ritual is a small decision<br />
              <em className="italic text-terracotta-light">made in advance,</em><br />
              so the tired version<br />
              of you doesn't have to."
            </p>
            <p className="overline text-cream/60 mt-10">— Stillform</p>
          </div>
        </div>
      </section>

      {/* Closing collection waitlist — repeated for the scroller who skipped the top */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 bg-cream border-t border-hairline text-center">
        <p className="overline mb-6 text-terracotta">The Rest Collection · December 2026</p>
        <h3 className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-ink leading-[1.0] mb-8 max-w-3xl mx-auto">
          Rest is coming this <em className="italic text-forest">December.</em>
        </h3>
        <p className="text-charcoal font-light leading-[1.75] max-w-xl mx-auto mb-10 text-base md:text-lg">
          Our first collection of considered rituals for slower evenings, deeper
          rest, and everyday restoration. Join the waitlist for first access.
        </p>
        <button
          onClick={() => setWaitlistFor(collectionPseudo)}
          data-testid="collection-waitlist-cta-bottom"
          className="inline-flex items-center gap-2 text-[0.72rem] tracking-[0.24em] uppercase bg-forest text-cream px-8 py-4 hover:bg-terracotta transition-colors"
        >
          Join the Rest waitlist <ArrowUpRight size={14} />
        </button>
      </section>

      <WaitlistModal
        open={!!waitlistFor}
        onClose={() => setWaitlistFor(null)}
        product={waitlistFor}
      />
    </div>
  );
}

function ComingSoonRitual({ slug }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);

  const titles = {
    ground: {
      name: "Ground",
      tagline: "Presence, calm and the sensory everyday.",
      body: "Bath, breath and the slow return to the body. In formulation.",
    },
    restore: {
      name: "Restore",
      tagline: "Recovery and nourishment, read closely.",
      body: "After exertion, after illness, after the long week. In formulation.",
    },
    daily: {
      name: "Daily",
      tagline: "Morning and evening wellness, considered.",
      body: "AM and PM wellness sachets. Composed. Repeated. Everyday.",
    },
  };
  const r = titles[slug];
  if (!r) return <Navigate to="/rituals" replace />;

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { subscribeNewsletter } = await import("@/lib/api");
      await subscribeNewsletter(email.trim().toLowerCase());
      setDone(true);
      toast.success("You're on the list.");
    } catch {
      toast.error("Didn't go through — please try again.");
    }
  };

  return (
    <div data-testid={`ritual-coming-${slug}`} className="pt-28 md:pt-32 min-h-[80vh]">
      <Seo
        path={`/rituals/${slug}`}
        title={`${r.name} — Coming soon`}
        description={r.tagline}
      />
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-28">
        <Link to="/rituals" className="overline inline-flex items-center gap-2 text-stone hover:text-ink mb-10">
          ← All Rituals
        </Link>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <p className="overline mb-6">Coming soon</p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-[8rem] font-light leading-[0.92] tracking-[-0.03em] text-ink">
              {r.name}.
            </h1>
            <p className="mt-8 font-display italic text-xl md:text-3xl text-charcoal leading-snug max-w-xl">
              {r.tagline}
            </p>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <p className="text-charcoal font-light leading-[1.75] mb-10">{r.body}</p>
            {done ? (
              <p className="overline text-moss">You're on the list.</p>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid={`notify-form-${slug}`}>
                <label className="block text-[0.62rem] tracking-[0.28em] uppercase text-charcoal">
                  Notify me when this opens
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email"
                  className="w-full border-b border-hairline bg-transparent py-3 text-ink outline-none focus:border-ink transition-colors placeholder:text-stone"
                  data-testid="notify-email"
                />
                <button
                  type="submit"
                  className="mt-4 inline-flex items-center gap-2 text-[0.72rem] tracking-[0.24em] uppercase bg-ink text-bone px-6 py-3 hover:bg-charcoal transition-colors"
                  data-testid="notify-submit"
                >
                  Notify me <ArrowRight size={14} />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
